import {
    Checkbox,
    DraggableList,
    DraggableListItem,
    Text,
    type DraggableItem,
} from '@amelio/ui-library';

import QuestionDraggableOption from '../QuestionDraggableOption/QuestionDraggableOption';

import type { MultiChoiceOption } from '@/types/mcqOptionType';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

const MIN_MULTI_CHOICE_OPTIONS = 2;

type McqOptionProps = {
    options: DraggableItem<MultiChoiceOption>[];
    setOptions: (opts: DraggableItem<MultiChoiceOption>[]) => void;
    updateOption: (id: string, value: string) => void;
    deleteOption: (id: string) => void;
    addOptionHandler: (index: number) => void;
    handleOtherChange: (checked: boolean) => void;
    isOtherOption: boolean;
    hideOtherOption?: boolean;
};
const McqOptionList = ({
    addOptionHandler,
    deleteOption,
    handleOtherChange,
    options,
    setOptions,
    updateOption,
    isOtherOption,
    hideOtherOption,
}: McqOptionProps) => {
    const { t } = useAmelioTranslations();
    const companyLanguage = useSelectedAppLanguage();
    // A multiple-choice question keeps a floor of answers: below it, the remove control goes away
    // rather than refusing the click.
    const canRemoveOption = options.length > MIN_MULTI_CHOICE_OPTIONS;

    return (
        <div>
            <Text
                className={'mt-8'}
                weight={'bold'}
            >
                {t('evaluation360Questionnaire.questionForm.questions.multipleChoice.longTitle')}
            </Text>

            <Text
                className={'mt-2'}
                weight={'light'}
                size={'small'}
            >
                {t('evaluation360Questionnaire.questionForm.questions.multipleChoice.description')}
            </Text>

            <div className={'mt-6 max-w-80'}>
                <DraggableList
                    items={options}
                    onReorder={setOptions}
                    useDragHandle
                    renderItem={(item, itemMeta) => (
                        <DraggableListItem isDragging={itemMeta.isDragging}>
                            <QuestionDraggableOption
                                value={getTextByLanguage(item.data.label, companyLanguage)}
                                dataTestId={`checkbox-option-${itemMeta.index}`}
                                onChange={(value) => updateOption(item.id, value)}
                                onRemove={() => deleteOption(item.id)}
                                canAddNewOption
                                canRemove={canRemoveOption}
                                inputWidth={'qdo-input'}
                                isLastOption={itemMeta.index === options.length - 1}
                                onAdd={() => addOptionHandler(itemMeta.index)}
                            />
                        </DraggableListItem>
                    )}
                />

                {!hideOtherOption && (
                    <div className={'mt-4'}>
                        <Checkbox
                            dataTestId={'other-option-checkbox'}
                            label={t(
                                'evaluation360Questionnaire.questionForm.questions.common.otherOptionLabel',
                            )}
                            checked={isOtherOption}
                            onChange={(checked: boolean) => handleOtherChange(checked)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default McqOptionList;
